import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { WindowRef } from '@spartacus/core';

import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';

import { IOrderProduct, IOrderReturnDetail, IOrderReturnDetailProduct, SdOrderReturnComponent } from '@seb/web-design-system/account';
import { SdButtonComponent, SdTitleComponent, StatusEnum } from '@seb/web-design-system/core';

import { CustomConsignment, ReturnEntry } from '@seb/web-common/core/models';
import { CartGroupTypeEnum } from '@seb/web-common/core/enums';
import { DocumentMapper, OrderMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, ToasterService, TrackingService } from '@seb/web-common/core/services';
import { OrderFacade } from '@seb/web-common/core/facades';

@Component({
  selector: 'wc-return-products',
  templateUrl: './return-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdButtonComponent,
    RouterLink,
    SdTitleComponent,
    SdOrderReturnComponent,
    TranslatePipe
  ]
})
export class ReturnProductsComponent implements OnInit, OnDestroy {
  products: IOrderProduct[];
  returnReasons: IOrderReturnDetail;
  submitted = false;

  private consignment: CustomConsignment;
  private orderCode: string;

  private returnedConsignment: Subject<CustomConsignment> = new BehaviorSubject(undefined);

  protected readonly destroy$: Subject<void> = new Subject();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private orderService: OrderFacade,
    private orderMapper: OrderMapper,
    private toasterService: ToasterService,
    private router: Router,
    private windowRef: WindowRef,
    private trackingService: TrackingService,
    private documentMapper: DocumentMapper,
    private cdRef: ChangeDetectorRef,
    private baseStoreService: BaseStoreService
  ) {}

  ngOnInit(): void {
    this.orderCode = this.route.snapshot?.params.orderCode;
    const consignmentCode = this.route.snapshot?.params.consignmentCode;
    const entryNumbers = [];

    const entryGroups$ = this.orderService
      .getOrderDetails(this.orderCode)
      .pipe(
        map((orderDetails) => orderDetails?.entryGroups?.filter((root) => root.type === CartGroupTypeEnum.STANDALONE))
      );

    const orderReturn$ = this.orderService.getOrderReturnConsignment(this.orderCode, consignmentCode).pipe(
      tap((consignment) => (this.consignment = consignment)),
      switchMap(() => this.orderService.getOrderReturnReasons())
    );

    combineLatest([entryGroups$, orderReturn$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([entryGroups, reasons]) => {
        const baseStore = this.baseStoreService.baseStore;
        entryGroups?.forEach((root) => root.entries?.forEach((entry) => entryNumbers.push(entry.entryNumber)));
        const isNewPriceManagementActivated = baseStore.activateNewPriceManagement;

        this.products = this.consignment.entries
          .filter((entry) => entryNumbers.includes(entry.orderEntry.entryNumber))
          .map((entry) => this.orderMapper.orderEntryToIOrderProduct(
            entry.orderEntry, entry.shippedQuantity,
            undefined, undefined, undefined, undefined, undefined,
            isNewPriceManagementActivated
          ));
        this.returnReasons = {
          items: reasons,
          valueKey: 'refundReason',
          libelleKey: 'reason',
          required: true
        };
        this.cdRef.markForCheck();
      });
  }

  returnDetails(returns: IOrderReturnDetailProduct[]): void {
    this.submitOrder(returns);

    this.returnedConsignment
      .pipe(first((consignment) => !!consignment))
      .pipe(takeUntil(this.destroy$))
      .subscribe((consignment) => {
        if (consignment.returnDocument) {
          this.submitted = true;
          this.consignment = consignment;
        } else {
          this.noReturnProduct();
        }
        this.cdRef.markForCheck();
      });
  }

  noReturnProduct(): void {
    this.toasterService.sendToasterEvent({
      status: StatusEnum.success,
      title: 'return.toast.addedTitle',
      description: 'return.toast.addedReturnDescription'
    });
    this.router.navigateByUrl('/my-orders');
  }

  printReturnDocument(): void {
    const url = this.documentMapper.normalizeUrl(this.consignment?.returnDocument?.url);
    this.windowRef.nativeWindow.open(url);
  }

  printReturnTicket(): void {
    const url = this.documentMapper.normalizeUrl(this.consignment?.returnTicket?.url);
    this.windowRef.nativeWindow.open(url);
  }

  ngOnDestroy(): void {
    this.returnedConsignment.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
  private submitOrder(returns: IOrderReturnDetailProduct[]): void {
    const returnEntries: ReturnEntry[] = returns.map((returnEntry) => {
      this.trackingService.sendEvent('clickReturnDetails', { productId: returnEntry.productCode });
      return {
        entryNumber: this.consignment.entries.find(
          (entry) => entry.orderEntry.product.commercialCode === returnEntry.productCode
        ).orderEntry.entryNumber,
        quantity: returnEntry.quantity,
        refundReason: returnEntry.reason
      };
    });

    returns.forEach((returnEntry) =>
      this.trackingService.sendEvent('clickReturnDetails', { productId: returnEntry.productCode })
    );

    this.orderService
      .submitOrderReturn(this.orderCode, this.consignment.code, returnEntries)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (consignment) => this.returnedConsignment.next(consignment),
        error: () => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.error,
            title: 'return.toast.errorTitle',
            description: 'return.toast.errorReturnDescription'
          });
        }
      });
  }

  onOpenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }
}
